import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, KeyRound, ShieldCheck, RotateCw } from "lucide-react";
import Cookies from "js-cookie";
import { SendHROtp, VerifyHROtp } from "../Services/AuthService";
import HRLoginFormImage from "../../../assests/HRLoginImage.png";
import XPRSurveyGreen from "../../../assests/XPRSurveyGreen.png";
import { useUser } from "../../../Utility/userContext";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 300;

const HRPortalPatterns = () => {
    return (
        <svg
            className="pointer-events-none absolute inset-0 z-0 h-full w-full"
            viewBox="0 0 1000 1000"
            preserveAspectRatio="xMidYMid slice"
            fill="none"
            aria-hidden="true"
        >
            {/* =========================================================
                COLOR DEFINITIONS
            ========================================================== */}
            <defs>
                <linearGradient
                    id="patternPurple"
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="1"
                >
                    <stop offset="0%" stopColor="#c2a8f0" />
                    <stop offset="100%" stopColor="#7f35b2" />
                </linearGradient>

                {/* Small dotted grid */}
                <pattern
                    id="dotPattern"
                    width="28"
                    height="28"
                    patternUnits="userSpaceOnUse"
                >
                    <circle
                        cx="4"
                        cy="4"
                        r="2.4"
                        fill="#b88be9"
                        opacity="0.65"
                    />
                </pattern>

                {/* Small plus pattern */}
                <pattern
                    id="plusPattern"
                    width="60"
                    height="60"
                    patternUnits="userSpaceOnUse"
                >
                    <path
                        d="M30 21V39M21 30H39"
                        stroke="#b88be9"
                        strokeWidth="2"
                        strokeLinecap="round"
                        opacity="0.45"
                    />
                </pattern>
            </defs>

            {/* =========================================================
                BACKGROUND DOT AREAS
            ========================================================== */}
            <rect
                x="10"
                y="10"
                width="180"
                height="180"
                fill="url(#dotPattern)"
                opacity="0.8"
            />

            <rect
                x="50"
                y="780"
                width="280"
                height="220"
                fill="url(#dotPattern)"
            />

            <rect
                x="260"
                y="600"
                width="120"
                height="120"
                fill="url(#dotPattern)"
                opacity="0.55"
            />

            <rect
                x="700"
                y="300"
                width="130"
                height="130"
                fill="url(#dotPattern)"
                opacity="0.55"
            />

            {/* =========================================================
                TOP-LEFT DIAGONAL LINES
            ========================================================== */}

            <g
                stroke="url(#patternPurple)"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.35"
            >
                <path d="M85 120L155 50" />
                <path d="M110 135L180 65" />

                <path d="M160 150L225 85" />
                <path d="M180 165L245 100" />
            </g>

            {/* =========================================================
                TOP CENTER / RIGHT DIAGONALS
            ========================================================== */}

            <g
                stroke="url(#patternPurple)"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.38"
            >
                <path d="M560 0L400 160" />
                <path d="M590 0L430 160" />

                <path d="M760 150L690 220" />
                <path d="M785 165L715 235" />

                <path d="M950 110L850 210" />
                <path d="M975 130L875 230" />
            </g>

            {/* =========================================================
                BOTTOM DIAGONAL LINES
            ========================================================== */}

            <g
                stroke="#b88be9"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.38"
            >
                <path d="M60 820L150 730" />
                <path d="M85 845L175 755" />

                <path d="M600 1000L770 830" />
                <path d="M630 1000L800 830" />

                <path d="M840 720L940 620" />
                <path d="M865 745L965 645" />
            </g>

            {/* =========================================================
                OUTLINED CIRCLES
            ========================================================== */}

            <g
                stroke="#9d5fd1"
                strokeWidth="2.2"
                opacity="0.42"
            >
                <circle cx="450" cy="150" r="20" />
                <circle cx="520" cy="365" r="10" />
                <circle cx="155" cy="410" r="9" />
                <circle cx="810" cy="60" r="13" />
                <circle cx="930" cy="250" r="9" />
                <circle cx="490" cy="885" r="11" />
            </g>

            {/* =========================================================
                SMALL PLUS / CROSS SHAPES
            ========================================================== */}

            <g
                stroke="#a36bdd"
                strokeWidth="2.2"
                strokeLinecap="round"
                opacity="0.55"
            >
                <path d="M230 170V194M218 182H242" />
                <path d="M390 45V65M380 55H400" />
                <path d="M655 115V137M644 126H666" />
                <path d="M725 270V296M712 283H738" />
                <path d="M65 680V706M52 693H78" />
                <path d="M910 820V844M898 832H922" />
                <path d="M330 780V804M318 792H342" />
            </g>

            {/* =========================================================
                FOUR POINT STAR
            ========================================================== */}

            <g
                stroke="#a36bdd"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.48"
            >
                <path
                    d="
                        M290 120
                        C290 132 290 132 302 132
                        C290 132 290 132 290 145
                        C290 132 290 132 278 132
                        C290 132 290 132 290 120
                    "
                />

                <path
                    d="
                        M780 480
                        C780 492 780 492 792 492
                        C780 492 780 492 780 505
                        C780 492 780 492 768 492
                        C780 492 780 492 780 480
                    "
                />
            </g>

            {/* =========================================================
                CHEVRONS
            ========================================================== */}

            <g
                stroke="#b789e6"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
            >
                <path d="M675 140L700 165L675 190" />
                <path d="M693 140L718 165L693 190" />
                <path d="M711 140L736 165L711 190" />

                <path d="M5 260L28 285L5 310" />
                <path d="M20 260L43 285L20 310" />
            </g>

            {/* =========================================================
                WAVE LINES
            ========================================================== */}

            <g
                stroke="#b789e6"
                strokeWidth="2.2"
                fill="none"
                strokeLinecap="round"
                opacity="0.45"
            >
                <path d="M35 370C45 360 55 380 65 370C75 360 85 380 95 370" />
                <path d="M35 385C45 375 55 395 65 385C75 375 85 395 95 385" />

                <path d="M550 890C560 880 570 900 580 890C590 880 600 900 610 890" />
                <path d="M550 905C560 895 570 915 580 905C590 895 600 915 610 905" />
            </g>

            {/* =========================================================
                TARGET / RADAR SHAPE
            ========================================================== */}

            <g
                transform="translate(790 350)"
                stroke="#a36bdd"
                fill="none"
                opacity="0.3"
            >
                <circle r="42" strokeWidth="2" />
                <circle r="30" strokeWidth="2" />
                <circle r="17" strokeWidth="2" />

                <path d="M0 -42V42" strokeWidth="1.5" />
                <path d="M-42 0H42" strokeWidth="1.5" />

                <circle cx="-30" cy="-30" r="4" fill="#a36bdd" />
                <circle cx="30" cy="-30" r="4" fill="#a36bdd" />
                <circle cx="30" cy="30" r="4" fill="#a36bdd" />
                <circle cx="-30" cy="30" r="4" fill="#a36bdd" />
            </g>

            {/* =========================================================
                SMALL DECORATIVE RINGS
            ========================================================== */}

            <g
                stroke="#b789e6"
                strokeWidth="2"
                opacity="0.3"
            >
                <circle cx="95" cy="250" r="7" />
                <circle cx="350" cy="300" r="5" />
                <circle cx="875" cy="540" r="8" />
                <circle cx="160" cy="900" r="6" />
                <circle cx="690" cy="730" r="5" />
            </g>

            {/* =========================================================
                FAINT PLUS GRID OVERLAY
            ========================================================== */}

            <rect
                x="0"
                y="0"
                width="1000"
                height="1000"
                fill="url(#plusPattern)"
                opacity="0.12"
            />
        </svg>
    );
};

const HRLogin = () => {
    const navigate = useNavigate();
    const { refreshUser } = useUser();
    const [step, setStep] = useState("email");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [isSending, setIsSending] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState("");
    const [cooldown, setCooldown] = useState(0);

    const otpRefs = useRef([]);
    const isOtpComplete = otp.every((digit) => digit !== "");

    // Auto-redirect if user already has a valid session
    useEffect(() => {
        const token = Cookies.get("JWTToken");
        if (!token) return;
        try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            if (payload.exp * 1000 > Date.now()) {
                navigate("/Company/dashboard", { replace: true });
            }
        } catch (e) {
            // Invalid token — stay on login page
        }
    }, [navigate]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const sendOtp = async (e) => {
        e?.preventDefault();
        if (!email) return;

        setError("");
        setIsSending(true);
        try {
            const response = await SendHROtp({ Email: email });
            if (response?.data?.status === "success") {
                setStep("otp");
                setOtp(Array(OTP_LENGTH).fill(""));
                setCooldown(RESEND_COOLDOWN_SECONDS);
                setTimeout(() => otpRefs.current[0]?.focus(), 100);
                CustomToast("success", response?.data?.message || "OTP sent to your email");
            } else {
                CustomToast("fail", response?.data?.message || "Invalid email.");
            }
        } catch (err) {
            CustomToast(
                "fail",
                err?.response?.data?.message || err?.message || "Couldn't send OTP. Please check the email and try again.",
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const next = [...otp];
        next[index] = value.slice(-1);
        setOtp(next);

        if (value && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasted) return;
        e.preventDefault();
        const next = Array(OTP_LENGTH).fill("");
        pasted.split("").forEach((digit, i) => {
            next[i] = digit;
        });
        setOtp(next);
        otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const verifyOtp = async (e) => {
        e.preventDefault();
        const code = otp.join("");
        if (code.length !== OTP_LENGTH) {
            setError("Please enter the complete 6-digit code.");
            return;
        }

        setError("");
        setIsVerifying(true);

        try {
            const response = await VerifyHROtp(email, code);
            const data = response?.data;
            if (data?.status === "success") {
                if (data?.jwtToken) {
                    Cookies.set("JWTToken", data.jwtToken, {
                        expires: 1,
                        secure: false,
                        sameSite: "Lax",
                    });
                }

                if (data?.tokenDetails) {
                    Cookies.set("userID", data.tokenDetails.userID, { expires: 1, secure: false, sameSite: "Lax" });
                    Cookies.set("userRole", data.tokenDetails.userRole, { expires: 1, secure: false, sameSite: "Lax" });
                    Cookies.set("tokenExpiry", data.tokenDetails.tokenExpiry, { expires: 1, secure: false, sameSite: "Lax" });
                }

                refreshUser();
                CustomToast("success", data?.message || "Successfully login.");
                navigate("/Company/dashboard");
            } else {
                CustomToast("fail", data?.message || "Invalid OTP code.");
            }
        } catch (err) {
            CustomToast("fail", err?.response?.data?.message || err?.message || "Incorrect or expired OTP. Please try again.");
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-white lg:flex">

            <div className="hidden lg:flex lg:w-[46%] relative flex-col justify-evenly overflow-hidden rounded-tr-[250px] rounded-bl-[250px] bg-gradient-to-br from-[#c2a8f026] via-[#c2a8f026] to-[#c2a8f033] px-10 py-12 xl:px-16 xl:py-14">
                <HRPortalPatterns />
                <div className="relative z-10">
                    <img
                        src={XPRSurveyGreen}
                        loading="lazy"
                        alt="Logo"
                        className="mb-4 w-28"
                    />
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="relative z-10 max-w-md"
                >
                    <h1 className="mb-4 text-4xl font-extrabold leading-tight xl:text-5xl">
                        <span className="text-[#7f35b2]">HR</span>{" "}
                        <span className="text-[#000000]">Portal</span>
                    </h1>
                    <p className="text-lg text-gray-600">
                        Streamline HR operations and empower your workforce.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="relative z-10 flex justify-center"
                >
                    <img
                        src={HRLoginFormImage}
                        alt="HR Portal dashboard preview"
                        className="w-full max-w-xl object-contain"
                    />
                </motion.div>
            </div>

            <div className="relative flex min-h-screen flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-6 lg:py-12">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <div className="mb-5 flex items-center justify-center lg:hidden">
                        <img src={XPRSurveyGreen} loading="lazy" alt="Logo" className="h-auto w-28" />
                    </div>

                    <div className="rounded-3xl border border-[#c2a8f033] bg-white p-5 shadow-xl shadow-[#7f35b2]/10 sm:p-8 lg:p-10">
                        <AnimatePresence mode="wait">
                            {step === "email" ? (
                                <motion.div
                                    key="email-step"
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -12 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <h2 className="mb-2 text-2xl font-bold text-[#000000] sm:text-3xl">Welcome!</h2>
                                    <p className="mb-6 text-sm text-gray-500 sm:mb-8 sm:text-base">
                                        Enter your email to receive a login code
                                    </p>

                                    <form onSubmit={sendOtp}>
                                        <label htmlFor="email" className="mb-2 block text-sm font-semibold text-gray-700">
                                            Email Address
                                        </label>
                                        <div className="relative mb-2">
                                            <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                                            <input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                required
                                                className="w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-4 text-gray-700 placeholder:text-gray-400 transition-colors focus:border-[#7f35b2] focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/30"
                                            />
                                        </div>

                                        {error && <p className="mb-2 mt-2 text-sm font-medium text-red-500">{error}</p>}

                                        <button
                                            type="submit"
                                            disabled={isSending || !email}
                                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#7f35b2] py-3.5 font-semibold text-white transition-colors hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {isSending ? "Sending OTP..." : "Send OTP"}
                                        </button>
                                    </form>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="otp-step"
                                    initial={{ opacity: 0, x: 12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 12 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <h2 className="mb-2 text-2xl font-bold text-[#000000] sm:text-3xl">Enter Code</h2>
                                    <p className="mb-6 text-sm text-gray-500 sm:mb-8 sm:text-base">
                                        We sent a 6-digit code to <span className="font-semibold text-gray-700">{email}</span>
                                    </p>

                                    <form onSubmit={verifyOtp}>
                                        <div className="mb-2 flex justify-between gap-1.5 sm:gap-2" onPaste={handleOtpPaste}>
                                            {otp.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={(el) => {
                                                        otpRefs.current[index] = el;
                                                    }}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                    className="h-12 w-10 rounded-xl border border-gray-200 text-center text-lg font-semibold text-[#000000] transition-colors focus:border-[#7f35b2] focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/30 sm:h-14 sm:w-12 sm:text-xl"
                                                />
                                            ))}
                                        </div>

                                        {error && <p className="mb-2 mt-2 text-sm font-medium text-red-500">{error}</p>}

                                        <button
                                            type="submit"
                                            disabled={isVerifying || !isOtpComplete}
                                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#7f35b2] py-3.5 font-semibold text-white transition-colors hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <KeyRound className="h-4 w-4" />
                                            {isVerifying ? "Verifying..." : "Verify & Login"}
                                        </button>
                                    </form>

                                    <button
                                        type="button"
                                        onClick={sendOtp}
                                        disabled={cooldown > 0 || isSending}
                                        className="mt-5 flex w-full items-center justify-center gap-1.5 text-sm font-medium text-[#7f35b2] hover:text-[#3bb290] hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
                                    >
                                        <RotateCw className={`h-3.5 w-3.5 ${isSending ? "animate-spin" : ""}`} />
                                        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="mt-5 flex items-center justify-center gap-2 px-2 text-center text-sm text-gray-400 sm:mt-6">
                        <ShieldCheck className="h-4 w-4 text-[#7f35b2]" />
                        Secure Login <span className="text-gray-300">•</span> Your data is safe with us
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default HRLogin;
